import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ResultOf } from 'gql.tada'
import { usePostHog } from 'posthog-js/react'

import { graphql_call } from '@repo/common/queries/schemas/server_user_query'

import { useAuth } from '../auth'

import {
  mutation_register_wallet,
  query_get_risk_check,
  query_get_wallet,
} from './wallet_graphql'

export const keys = createQueryKeyStore({
  wallet: {
    registered: null,
    risk_check: null,
  },
})

type WalletType = {
  address: CryptoAddress
  provider: string
}

export function useGetRegisteredWallet() {
  const auth = useAuth()

  return useQuery<WalletType | null>({
    queryKey: keys.wallet.registered.queryKey,
    queryFn: async () => {
      const results = await graphql_call({ query: query_get_wallet })
      return results?.wallet || null
    },
    enabled: auth.data?.is_authenticated,
  })
}

export function useRegisterWallet() {
  const posthog = usePostHog()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { address: CryptoAddress; provider: string }) => {
      const result = await graphql_call({
        query: mutation_register_wallet,
        variables: {
          input,
        },
      })
      posthog.capture('$set', {
        $set: {
          wallet_address: input.address,
          wallet_provider: input.provider,
        },
      })
      return result
    },
    onSettled: () => {
      void queryClient.invalidateQueries(keys.wallet.registered)
    },
  })
}

export type WalletRiskType = ResultOf<
  typeof query_get_risk_check
>['me']['wallet_risk_check']['verification']

export function useGetWalletRiskCheck({
  refetch_until_completed,
}: {
  refetch_until_completed?: boolean
} = {}) {
  const auth = useAuth()

  return useQuery<WalletRiskType | undefined>({
    queryKey: keys.wallet.risk_check.queryKey,
    queryFn: async () => {
      const results = await graphql_call({ query: query_get_risk_check })
      return results?.wallet_risk_check.verification
    },
    enabled: auth.data?.is_authenticated,
    refetchInterval: (query) => {
      return query.state.data?.status != 'completed' && refetch_until_completed
        ? 5000
        : false
    },
  })
}
